import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { CloudUpload } from '@material-ui/icons';
import StepperButtons from '../acquisition/StepperButtons';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import GoogleMap from '../map/GoogleMap';
import ShapeEditControl from '../map/ShapeEditControl';
import { useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import * as Map from '../../../common/map';
import { aoiAreaKm2, aoiGeometry, isMultiPolygon, parseKmlArea } from '../../../common/aoi';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import { Actions as CSqueeze } from '../../../store/ducks/csqueeze';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import { getMangroves } from '../../../algorithms/csqueeze';

/**
 * @function computeAreaKm2
 * @description Computes a polygon's area in km², for the AOI performance warning.
 * @param {Array<Array<Number>>} coordinates - Polygon coordinates as [lng, lat] pairs.
 * **Called by:
 * @see {handleMapLoad}
 * @see {handleDrawing}
 * @see {readFileData}
 * @memberof module:Csqueeze
 * @returns {Number} Area in km².
 */
const computeAreaKm2 = (coordinates) => aoiAreaKm2(coordinates);

/**
 * @function AOIChooser
 * @description Second step of the CSqueeze acquisition wizard. Lets the user draw the Area of
 * Interest (AOI) on a Google Map — or import it from a KML file —, previews its performance
 * impact based on area, and optionally overlays a reference mangrove layer.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * @see {@link handleDrawing} - stores the drawn AOI.
 * @see {@link handleChoose} - validates and submits the step.
 * **Called by:
 * @see {CoastalSqueezePage} - as the second acquisition step.
 * @memberof module:Csqueeze
 * @returns {JSX.Element} The AOI chooser UI.
 */
const AOIChooser = ({ navigate, sessionImportButton, ...extra }) => {
  const dispatch = useDispatch();
  const mapRef = useRef();
  const mangroveLayer = useRef();

  const [t] = useTranslation();
  const savedCoordinates = useSelector((state) => state.acquisition.coordinates);
  const [overlay, setOverlay] = useState(null);
  const [coordinates, setCoordinates] = useState(() => savedCoordinates ?? null);
  const [areaOfPolygon, setAreaOfPolygon] = useState(null);

  const [kmlFileName, setKmlFileName] = useState('');
  const [kmlLoading, setKmlLoading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const [mangrove, setMangrove] = useState(false);

  const [geeErrorOpen, setGeeErrorOpen] = useState(false);

  /**
   * @description Lets the user drag the drawn AOI's existing points, keeping the coordinates and
   * the area warning in sync with each adjustment.
   * @see {@link useShapeVertexEditing}
   * @memberof module:Csqueeze
   */
  const aoiEditing = useShapeVertexEditing({
    onChange: (coords) => {
      setCoordinates(coords);
      setAreaOfPolygon(computeAreaKm2(coords));
    },
  });

  /**
   * @function handleMapLoad
   * @description Adjusts the shared GoogleMap to this step's look (zoom, full-screen control,
   * horizontal map-type bar, no points of interest), then either shows the drawing controls or,
   * when the user navigates back to this step, redraws the previously-committed AOI.
   * @param {google.maps.Map} map - The freshly created map instance.
   * @see {@link computeAreaKm2}
   * **Called by:
   * @see {AOIChooser} - via GoogleMap's onLoad.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleMapLoad = (map) => {
    mapRef.current = map;
    map.setOptions({
      zoom: 10,
      fullscreenControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_LEFT,
      },
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });

    if (!coordinates) {
      Map.setDrawingControlsVisible(true);
      return;
    }

    const openRing = Map.toOpenRing(coordinates);
    const overlayFixed = Map.drawOutline(openRing);
    setOverlay(overlayFixed);
    setCoordinates(openRing);
    setAreaOfPolygon(computeAreaKm2(openRing));
    aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');
    Map.setDrawingControlsVisible(false);
    Map.fitToCoordinates(openRing);
  };

  /**
   * @function handleDrawing
   * @description Stores the drawn AOI overlay and coordinates, hides the drawing controls, and
   * computes the AOI's area (in km²) for the performance warning message.
   * @param {Object} drawnOverlay - The drawn shape's overlay.
   * @param {Array<Array<Number>>} coords - The drawn shape's coordinates, as [lng, lat] pairs.
   * @see {@link computeAreaKm2}
   * **Called by:
   * @see {AOIChooser} - via GoogleMap's onRegionDrawn.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleDrawing = (drawnOverlay, coords, type) => {
    setOverlay(drawnOverlay);
    setCoordinates(coords);
    setAreaOfPolygon(computeAreaKm2(coords));
    aoiEditing.setShape(drawnOverlay, type);
    Map.setDrawingControlsVisible(false);
  };

  /**
   * @function handleUndo
   * @description Clears the drawn AOI overlay and re-enables the map's drawing controls.
   * **Called by:
   * @see {AOIChooser} - via the "Undo" button's onClick.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleUndo = () => {
    aoiEditing.setShape(null);
    overlay.setMap(null);
    setOverlay(null);
    setCoordinates(null);
    setAreaOfPolygon(null);
    Map.setDrawingControlsVisible(true);
  };

  /**
   * @function handleChoose
   * @description Dispatches the drawn AOI to the acquisition state, opening the GEE error dialog
   * if the Earth Engine geometry construction fails.
   * **Called by:
   * @see {AOIChooser} - via StepperButtons' onNext.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleChoose = () => {
    if (JSON.stringify(coordinates) !== JSON.stringify(savedCoordinates)) {
      window.sessionStorage.removeItem('csqueezeSelectedDates');
      window.sessionStorage.removeItem('csqueezeCloudCoverage');
      window.sessionStorage.removeItem('csqueezeActiveTab');
      dispatch(CSqueeze.setYears([]));
      dispatch(CSqueeze.setDates([]));
      dispatch(CSqueeze.setMissions([]));
    }
    aoiEditing.stopEditing();
    try {
      dispatch(Acquisition.setAOI(overlay, coordinates, aoiGeometry(coordinates), kmlFileName));
    } catch (e) {
      setGeeErrorOpen(true);
    }
  };

  /**
   * @function handleMangrove
   * @description Toggles the reference mangrove overlay on the map.
   * @param {boolean|Event} e - Either the desired visibility, or a checkbox change event.
   * @see {@link getMangroves}
   * **Called by:
   * @see {AOIChooser} - via the mangrove checkbox row's onClick.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleMangrove = (e) => {
    let visible = typeof e === 'boolean' ? e : e.target.checked;
    if (!mangroveLayer.current) mangroveLayer.current = getMangroves();

    if (visible) {
      mapRef.current.overlayMapTypes.push(mangroveLayer.current);
    } else {
      mapRef.current.overlayMapTypes.clear();
    }
    setMangrove(visible);
  };

  const drawn = Boolean(coordinates);

  const steps = [
    {
      selector: '#areachooser',
      content: t('tour.csqueeze.2.aoi'),
    },
    {
      selector: '#mangrove',
      content: t('tour.csqueeze.2.mangrove'),
    },
    {
      selector: '#importKml',
      content: t('tour.csqueeze.2.importKml'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showROITour', true);

  /**
   * @function readFileData
   * @description Reads the selected KML file, extracts its polygon coordinates, and sets it as
   * the current AOI on the map — same one-click file-input pattern used in Bathymetry's
   * AOIChooser (no intermediate confirmation dialog).
   * @param {Event} e - The file input's change event.
   * **Called by:
   * @see {AOIChooser} - via the KML file input's onChange.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const readFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setKmlFileName(file.name);
    setKmlLoading(true);
    const reader = new FileReader();

    reader.addEventListener(
      'load',
      function () {
        try {
          const fileContent = reader.result;
          if (fileContent.indexOf('<coordinates>') !== -1) {
            const coordinatesArray = parseKmlArea(fileContent);

            if (coordinatesArray) {
              let overlayFixed = Map.drawOutline(coordinatesArray);

              if (!overlayFixed) {
                openErrorDialog(t('forms.csqueeze.2.errorMessageDrawingOutline'));
                return;
              }

              overlayFixed.setOptions({
                fillColor: '#00B3A1',
                fillOpacity: 0.2,
                strokeColor: '#003832',
                strokeOpacity: 1,
                strokeWeight: 0.5,
              });

              const openRing = Map.toOpenRing(coordinatesArray);
              setOverlay(overlayFixed);
              setCoordinates(openRing);
              setAreaOfPolygon(computeAreaKm2(openRing));
              aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');

              Map.fitToCoordinates(openRing);
              Map.setDrawingControlsVisible(false);
            } else {
              openErrorDialog(t('forms.csqueeze.2.errorMessageNoValidCoord'));
            }
          } else {
            openErrorDialog(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
          }
        } finally {
          setKmlLoading(false);
        }
      },
      false,
    );

    reader.readAsText(file);
  };

  /**
   * @function openErrorDialog
   * @description Opens the generic error dialog with the given message.
   * @param {String} message - Localized error message to display.
   * **Called by:
   * @see {readFileData}
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const openErrorDialog = (message) => {
    setDialogMessage(message);
    setErrorDialogOpen(true);
  };

  /**
   * @function closeErrorDialog
   * @description Closes the generic error dialog.
   * **Called by:
   * @see {AOIChooser} - via the error dialog's "Close" button.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const closeErrorDialog = () => {
    setErrorDialogOpen(false);
  };

  /**
   * @function handleCloseGeeError
   * @description Closes the Google Earth Engine access error dialog.
   * **Called by:
   * @see {AOIChooser} - via the GEE error dialog's "Close" button.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleCloseGeeError = () => {
    setGeeErrorOpen(false);
  };

  return (
    <React.Fragment>
      <Box>
        <Box
          textAlign="center"
          display="flex"
          justifyContent="center"
          marginBottom={areaOfPolygon ? -9 : 0}
          marginTop={areaOfPolygon ? +3 : 0}
          style={{ color: 'white' }}>
          {areaOfPolygon && (
            <React.Fragment>
              {areaOfPolygon > 20 ? (
                <Typography
                  style={{
                    borderRadius: 10,
                    padding: '10px 20px',
                    backgroundColor: '#f02c2c',
                    fontWeight: 800,
                  }}>
                  {t('forms.csqueeze.2.performanceMessage')
                    .replace('{number}', areaOfPolygon.toFixed(2))
                    .replace('{level}', t('forms.csqueeze.2.slow').toUpperCase())}
                </Typography>
              ) : areaOfPolygon > 10 ? (
                <Typography
                  style={{
                    borderRadius: 10,
                    padding: '10px 20px',
                    backgroundColor: '#ec942c',
                    fontWeight: 800,
                  }}>
                  {t('forms.csqueeze.2.performanceMessage')
                    .replace('{number}', areaOfPolygon.toFixed(2))
                    .replace('{level}', t('forms.csqueeze.2.moderate').toUpperCase())}
                </Typography>
              ) : (
                <Typography
                  style={{
                    borderRadius: 10,
                    padding: '10px 20px',
                    backgroundColor: '#40a6ce',
                    fontWeight: 800,
                  }}>
                  {t('forms.csqueeze.2.performanceMessage')
                    .replace('{number}', areaOfPolygon.toFixed(2))
                    .replace('{level}', t('forms.csqueeze.2.fast').toUpperCase())}
                </Typography>
              )}
            </React.Fragment>
          )}
        </Box>

        <Dialog
          open={errorDialogOpen}
          onClose={closeErrorDialog}
          PaperProps={{ style: { padding: 20, borderRadius: 12 } }}>
          <DialogContent style={{ textAlign: 'center' }}>
            <h2 style={{ marginTop: 0 }}>{t('forms.csqueeze.2.errorTitle')}</h2>
            <p style={{ fontSize: 18 }}>{dialogMessage}</p>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={closeErrorDialog} color="secondary" variant="contained">
              {t('forms.csqueeze.2.close')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={geeErrorOpen}
          onClose={handleCloseGeeError}
          aria-labelledby="gee-error-title"
          aria-describedby="gee-error-description"
          PaperProps={{ style: { padding: 20, borderRadius: 12, textAlign: 'center' } }}>
          <DialogTitle id="gee-error-title" style={{ textAlign: 'center' }}>
            {t('forms.csqueeze.2.geeError.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="gee-error-description" style={{ fontSize: '16px' }}>
              {t('forms.csqueeze.2.geeError.content')}
              <a href="https://earthengine.google.com/signup/" target="_blank" rel="noopener noreferrer">
                https://earthengine.google.com/signup/
              </a>
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={handleCloseGeeError} color="primary" variant="outlined">
              {t('forms.csqueeze.2.geeError.close')}
            </Button>
          </DialogActions>
        </Dialog>

        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ padding: '16px 0 16px 24px' }}>
          <Box id="importKml" style={{ maxWidth: 340 }}>
            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {t('forms.csqueeze.2.aoiFileTitle')}
              </Typography>
              <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                *
              </Typography>
            </Box>
            <Button variant="outlined" color="primary" component="label" startIcon={<CloudUpload />}>
              {t('forms.csqueeze.2.importTitle')}
              <input
                hidden
                id="file_import_kml"
                accept=".kml"
                type="file"
                onClick={(e) => (e.target.value = null)}
                onChange={readFileData}
              />
            </Button>
            <Typography variant="body2" style={{ marginTop: 10 }}>
              {kmlFileName ? (
                <>
                  {t('forms.csqueeze.2.fileLabel')} <strong>{kmlFileName}</strong>
                </>
              ) : (
                t('forms.csqueeze.2.noKmlFileSelected')
              )}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6 }}>
              {coordinates ? t('forms.csqueeze.2.aoiLoadedOnMap') : t('forms.csqueeze.2.drawOnMapOrImportKml')}
            </Typography>
          </Box>
          <HelpButton
            onClickFunction={() => {
              setIsTourOpen(true);
            }}
            extra={sessionImportButton}
          />
        </Box>
        <Box
          id="mangrove"
          style={{
            height: '40px',
            position: 'absolute',
            backgroundColor: '#FFF',
            boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
            borderRadius: '2px',
            border: '1px solid rgba(0, 0, 0, 0.3)',
            padding: '0px 10px 0px 0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '99',
            marginTop: '60px',
            marginLeft: '10px',
            cursor: 'pointer',
          }}
          sx={{ boxShadow: 12 }}
          onClick={() => handleMangrove(!mangrove)}>
          <Checkbox checked={mangrove} inputProps={{ 'aria-label': 'primary checkbox' }} />
          <Typography>{t('forms.csqueeze.2.viewMangrove')}</Typography>
        </Box>
        <div id="areachooser" style={{ width: '100%', height: 600 }}>
          <GoogleMap onRegionDrawn={handleDrawing} onLoad={handleMapLoad} />
        </div>
        <ShapeEditControl editing={aoiEditing} visible={drawn} />
        <StepperButtons
          navigate={navigate}
          nextDisabled={drawn === false || aoiEditing.geometryError !== ''}
          onNext={handleChoose}>
          <Button onClick={handleUndo} disabled={drawn === false} color="secondary" variant="outlined">
            {t('forms.csqueeze.2.undo')}
          </Button>
        </StepperButtons>
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Box>
      {kmlLoading && <ActivityIndicator forceShow={true} title={t('forms.core.loading.kml')} />}
    </React.Fragment>
  );
};

export default AOIChooser;
